import type { ComponentChildren } from "preact";
import { useLocation } from "preact-iso";
import { useEffect, useState } from "preact/hooks";
import { signal } from "@preact/signals";
import { isAuthenticated, authLoading } from "../store/auth.js";
import { apiFetch } from "../api/client.js";
import { LegalInterstitial } from "./LegalInterstitial.js";

interface Props {
  children: ComponentChildren;
}

interface LegalStatus {
  needs_consent: boolean;
  tos_current: string;
  privacy_current: string;
}

const legalStatus = signal<LegalStatus | null>(null);
const legalLoading = signal(false);
const legalChecked = signal(false);

export function resetLegalStatus(): void {
  legalStatus.value = null;
  legalLoading.value = false;
  legalChecked.value = false;
}

export function ProtectedRoute({ children }: Props) {
  const { route } = useLocation();
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!authLoading.value && !isAuthenticated.value) {
      route("/login", true);
    }
  }, [route, authLoading.value, isAuthenticated.value]);

  useEffect(() => {
    if (
      !authLoading.value &&
      isAuthenticated.value &&
      !legalChecked.value &&
      !legalLoading.value
    ) {
      legalLoading.value = true;
      apiFetch("/v1/account/legal-status")
        .then(async (res) => {
          if (res.ok) {
            const data = await res.json();
            legalStatus.value = data as LegalStatus;
          } else {
            setError(true);
          }
        })
        .catch(() => setError(true))
        .finally(() => {
          legalLoading.value = false;
          legalChecked.value = true;
        });
    }
  }, [authLoading.value, isAuthenticated.value]);

  if (authLoading.value) {
    return (
      <div class="flex h-screen items-center justify-center">Loading…</div>
    );
  }

  if (!isAuthenticated.value) {
    return null;
  }

  if (legalLoading.value || !legalChecked.value) {
    return (
      <div class="flex h-screen items-center justify-center">Loading…</div>
    );
  }

  if (error) {
    return <>{children}</>;
  }

  if (legalStatus.value?.needs_consent) {
    return (
      <LegalInterstitial
        tosVersion={legalStatus.value.tos_current}
        privacyVersion={legalStatus.value.privacy_current}
        onAccepted={() => {
          legalStatus.value = {
            ...legalStatus.value!,
            needs_consent: false,
          };
        }}
      >
        {children}
      </LegalInterstitial>
    );
  }

  return <>{children}</>;
}
